import { useState } from 'react';
import { Icon } from '@iconify/react';
import { Brain, Users, Shield, Zap, Play, Database, Search } from 'lucide-react';
import IntegrationLandingBase from '@/components/integrations/IntegrationLandingBase';

export default function TelegramLanding() {
  const [copiedCode, setCopiedCode] = useState(false);

  const handleCopySetupCode = async () => {
    const setupCode = `# 1. Add Memara Bot to Telegram
Search @MemараBot in Telegram

# 2. Start the bot in your chat
Send /start to @MemараBot

# 3. Start capturing memories
Forward messages or use /remember command

# 4. Search your memories
Use /search command to find anything`;
    try {
      await navigator.clipboard.writeText(setupCode);
      setCopiedCode(true);
      setTimeout(() => setCopiedCode(false), 2000);
    } catch (error) {
      const textArea = document.createElement('textarea');
      textArea.value = setupCode;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopiedCode(true);
      setTimeout(() => setCopiedCode(false), 2000);
    }
  };

  const handleGetStarted = () => {
    window.location.href = '/login';
  };

  const handleSeeDemo = () => {
    window.open('https://www.youtube.com/watch?v=dQw4w9WgXcQ', '_blank');
  };

  return (
    <IntegrationLandingBase
      registryIntegrationId="telegram_bot"
      icon={
        <span className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-50 shadow" aria-label="Telegram Logo">
          <Icon icon="logos:telegram" width={48} height={48} />
        </span>
      }
      title="Telegram Integration"
      description="Privacy-first messaging intelligence for secure communication workflows. Transform Telegram conversations, channels, and groups into searchable knowledge with lightning-fast bot integration, end-to-end encryption compatibility, and cross-platform memory synchronization."
      features={[
        { icon: <Zap className="h-4 w-4 text-blue-600" />, title: 'Lightning-Fast Message Capture', description: 'Forward any message to instantly create memories with sub-second processing and real-time synchronization across devices.' },
        { icon: <Brain className="h-4 w-4 text-blue-600" />, title: 'Intelligent Message Organization', description: 'AI-powered automatic organization of Telegram knowledge with semantic understanding and context-aware categorization.' },
        { icon: <Users className="h-4 w-4 text-blue-600" />, title: 'Multi-Channel Group Memory', description: 'Shared knowledge management for Telegram groups and channels with collaborative intelligence and role-based access controls.' },
        { icon: <Shield className="h-4 w-4 text-blue-600" />, title: 'Privacy-Focused Memory Storage', description: 'End-to-end encryption compatible memory storage with privacy-first architecture and secure multi-tenant isolation.' },
        { icon: <Database className="h-4 w-4 text-blue-600" />, title: 'Cross-Platform Intelligence', description: 'Unified memory access across Telegram Desktop, Mobile, and Web with intelligent synchronization and context preservation.' },
        { icon: <Search className="h-4 w-4 text-blue-600" />, title: 'Advanced Semantic Search', description: 'Natural language search across all Telegram memories with AI-powered context understanding and instant retrieval.' },
      ]}
      setupSteps={[
        'Add Memara Bot to your Telegram account or group',
        'Start the bot and configure privacy settings',
        'Begin capturing memories from messages and conversations',
        'Search and access your Telegram knowledge instantly',
      ]}
      ctas={[
        {
          label: '🚀 Get Started Free',
          onClick: handleGetStarted,
          className: 'bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 h-11 rounded-md text-base font-semibold',
        },
        {
          label: 'See Demo',
          onClick: handleSeeDemo,
          className: 'border-blue-200 text-blue-600 hover:bg-blue-50 px-8 py-4 h-11 rounded-md text-base border',
          icon: <Play className="w-4 h-4 mr-2" />,
        },
        {
          label: copiedCode ? 'Copied!' : 'Copy Setup Code',
          onClick: handleCopySetupCode,
          className: 'border-blue-200 text-blue-600 hover:bg-blue-50 px-8 py-4 h-11 rounded-md text-base border',
          icon: copiedCode ? <Brain className="w-4 h-4 mr-2" /> : <Icon icon="logos:telegram" width={20} height={20} />,
        },
      ]}
      videoUrl="https://www.youtube.com/watch?v=dQw4w9WgXcQ"
      microcopy="Trusted by privacy advocates, secure communication teams, and distributed organizations using Telegram for encrypted messaging and cross-platform collaboration."
      trustedBy="Telegram, Privacy-First Teams, and more."
      testimonial="Memara's Telegram integration provides the perfect balance of instant memory capture and privacy-first security, transforming our encrypted communications into an intelligent, searchable knowledge base without compromising our security standards."
    />
  );
}